import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { IconButton } from '@/components/ui/icon-button';

export interface PasswordFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  error?: ReactNode;
  hint?: ReactNode;
  /**
   * Show the "at least 12 characters" hint. On a new password, not on a login
   * field — telling someone the rules while they type an existing password is
   * noise.
   */
  showMinLengthHint?: boolean;
}

export const PASSWORD_MIN_LENGTH = 12;

/**
 * Password input with a show/hide toggle.
 *
 * The hint is length and nothing else. The shared SignupRequest schema says so
 * outright — "Minimum 12. Length beats character classes; do not add symbol
 * requirements" — and it is right: composition rules push people towards
 * `Password1!` and away from a passphrase. So there is no "must contain a
 * number", no strength meter theatre, and `autoComplete="new-password"` so the
 * browser's own generator is the path of least resistance.
 */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(
    { label = 'Password', error, hint, showMinLengthHint = false, id, ...props },
    ref,
  ) {
    const [visible, setVisible] = useState(false);
    const generatedId = useId();
    const fieldId = id ?? generatedId;

    const resolvedHint =
      hint ?? (showMinLengthHint ? `At least ${PASSWORD_MIN_LENGTH} characters.` : undefined);

    return (
      <Input
        ref={ref}
        id={fieldId}
        label={label}
        error={error}
        hint={resolvedHint}
        type={visible ? 'text' : 'password'}
        // A password manager that cannot tell sign-in from sign-up saves the
        // wrong thing; callers pass the right value explicitly.
        autoComplete={props.autoComplete ?? 'current-password'}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        trailing={
          <IconButton
            label={visible ? 'Hide password' : 'Show password'}
            icon={visible ? EyeOff : Eye}
            variant="ghost"
            size="md"
            // Never a submit button: this sits inside a form.
            type="button"
            // Toggling visibility should not steal focus from the field.
            tabIndex={0}
            onClick={() => setVisible((v) => !v)}
            // Announce the state change to assistive tech.
            aria-pressed={visible}
          />
        }
        {...props}
      />
    );
  },
);
